import { lazy, Suspense } from 'react';
import { Outlet, ScrollRestoration } from 'react-router-dom';
import { isNativePlatform } from '@lib/platform';
import { WebLogtoAdapterProvider, NativeLogtoAdapterProvider } from '@lib/logto';
import { AuthProvider } from '@providers';
import { BadgeProvider } from '@providers/BadgeProvider';
import { LocaleProvider } from '@providers/LocaleProvider';
import { InstallPromptProvider } from '@providers/InstallPromptProvider';
import { AnalyticsBootstrapper } from './AnalyticsBootstrapper';
import { AuthRouteOverlayReset } from './AuthRouteOverlayReset';
import { ReferralCodeSync } from './ReferralCodeSync';
import { AttributionCapture } from './AttributionCapture';
import { NativePushInitializer } from './NativePushInitializer';
import { NrefBootResolver } from './NrefBootResolver';
import { WebPushInitializer } from './WebPushInitializer';
import { InstallPromptInitializer } from './InstallPromptInitializer';
import { NativePostAuthHandler } from './NativePostAuthHandler';
import { RouteFallback } from './RouteFallback';
import { SignedOutByOtherTabDrawer } from './SignedOutByOtherTabDrawer';
import { EventStreamProvider } from '@providers/EventStreamProvider';
import { StableRouterProvider } from '@providers/StableRouterProvider';
import { featureFlags } from '@config/featureFlags';

// Its own path, never the `@providers` barrel: 20+ suites blanket-mock that
// barrel, and a mocked barrel would silently unmount the continuation watcher.
// Lazy, so the watcher and the status request stay out of the entry chunk.
const MagicLinkContinuationWatcher = lazy(() =>
  import('@providers/MagicLinkContinuationProvider').then(module => ({
    default: module.MagicLinkContinuationWatcher,
  })),
);

// AIDEV-NOTE: Platform-conditional adapter -- uses @logto/capacitor on native (RFC 8252
// system browser auth) and @logto/react on web (SPA redirect auth). Both expose the same
// LogtoAdapterValue interface via useLogtoAdapter().
const LogtoAdapterProvider = isNativePlatform()
  ? NativeLogtoAdapterProvider
  : WebLogtoAdapterProvider;

// AIDEV-NOTE: Root layout wraps entire app with providers, ScrollRestoration, and global Suspense
// ScrollRestoration is React Router's native component - requires data router (createBrowserRouter)
// getKey uses pathname so bottom nav tabs restore scroll when returning to same path
// Exception: when location.state.scrollToTop is true, use unique key to force scroll to top
export function RootLayout() {
  return (
    // StableRouterProvider is outermost so it covers every route: it holds the app's single router
    // subscription on behalf of the list leaves below, which navigate but must not re-render when
    // the URL moves (see stableRouterContext.ts).
    <StableRouterProvider>
      <LogtoAdapterProvider>
        <AuthProvider>
          {isNativePlatform() && <NativePostAuthHandler />}
          <EventStreamProvider>
            <LocaleProvider>
              {!isNativePlatform() && <SignedOutByOtherTabDrawer />}
              {/* Router-context bridge to the out-of-router overlay hosts in
                  index.tsx: nothing from the previous screen survives onto an
                  auth route. See the component for why the door hop needs it. */}
              <AuthRouteOverlayReset />
              <AnalyticsBootstrapper />
              {/* A leaf, lazily and only behind the flag: a flag-off build never
                  fetches it (F-1 of review 5330766393). Inside AuthProvider (it
                  reads the raw session flag) and inside StableRouterProvider (it
                  navigates and reads the URL without subscribing); it provides
                  no context and renders nothing, so it wraps nothing. */}
              {featureFlags.magicLinkContinuation && (
                <Suspense fallback={null}>
                  <MagicLinkContinuationWatcher />
                </Suspense>
              )}
              <ReferralCodeSync />
              <AttributionCapture />
              {isNativePlatform() && <NativePushInitializer />}
              <InstallPromptProvider>
                {!isNativePlatform() && <WebPushInitializer />}
                {!isNativePlatform() && <NrefBootResolver />}
                {!isNativePlatform() && <InstallPromptInitializer />}
                <ScrollRestoration
                  getKey={location => {
                    // Force scroll to top when explicitly requested via navigation state
                    // This is used after creating a question to ensure user sees the new content
                    const state = location.state as { scrollToTop?: boolean } | null;
                    if (state?.scrollToTop) {
                      // Return unique key so ScrollRestoration won't restore previous position
                      return `${location.pathname}-${location.key}`;
                    }
                    // Default: use pathname for consistent scroll restoration per page
                    return location.pathname;
                  }}
                />
                <BadgeProvider>
                  {/* Cream ground, no spinner, quiet until the wait is real.
                      See RouteFallback for why the old blue spinner had to go. */}
                  <Suspense fallback={<RouteFallback />}>
                    <Outlet />
                  </Suspense>
                </BadgeProvider>
              </InstallPromptProvider>
            </LocaleProvider>
          </EventStreamProvider>
        </AuthProvider>
      </LogtoAdapterProvider>
    </StableRouterProvider>
  );
}
